package com.appsyde.shared.presentation.screen.component.message.mapper

import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
fun DetectedFieldV2.keyNorm(): String =
    key?.trim().orEmpty().lowercase()
enum class SoftGroup { CUSTOMER, COMMANDE, PRODUIT_INCIDENT, PAIEMENT, LIVRAISON, PREUVES, OTHER }

val GROUP_ORDER = listOf(
    SoftGroup.CUSTOMER,
    SoftGroup.COMMANDE,
    SoftGroup.PRODUIT_INCIDENT,
    SoftGroup.PAIEMENT,
    SoftGroup.LIVRAISON,
    SoftGroup.PREUVES,
    SoftGroup.OTHER
)

/** Ordre d'affichage des champs dans chaque section (sidebar). */
fun DetectedFieldV2.sidebarFieldOrder(): Int = when (keyNorm()) {
    "customer_name" -> 0
    "customer_email" -> 1
    "email" -> 2
    "customer_phone" -> 3
    "phone" -> 4
    "account_id" -> 5
    "order_number" -> 0
    "order_id" -> 1
    "invoice_number" -> 2
    "date_reference" -> 3
    "order_timeframe" -> 4
    "timeframe" -> 5
    "missing_product_name" -> 6
    "product_name" -> 0
    "product_sku" -> 1
    "issue_type" -> 2
    "refund_preference" -> 3
    "payment_method" -> 0
    "payment_amount" -> 1
    "paid_amount" -> 2
    "amount" -> 3
    "delivery_status" -> 0
    "delivery_method" -> 1
    "tracking_number" -> 2
    "delivery_recipient" -> 3
    "delivery_address" -> 4
    "delivery_city" -> 5
    "delivery_postcode" -> 6
    "photo_proof" -> 0
    "delivery_proof" -> 1
    "login_identifier" -> 0
    "recent_security_events" -> 1
    "customer_note" -> 2
    else -> 100
}

fun DetectedFieldV2.uiValue(): String {
    val k = keyNorm()
    val v = value?.trim().orEmpty()
    if (v.isBlank()) return v

    return when (k) {
        // ✅ Issue type (codes -> FR)
        "issue_type" -> when (v.lowercase()) {
            "damaged" -> "Produit endommagé"
            "missing_item" -> "Article manquant"
            "wrong_item" -> "Mauvais article"
            else -> v
        }

        // ✅ Refund preference (codes -> FR)
        "refund_preference" -> when (v.lowercase()) {
            "refund" -> "Remboursement"
            "replacement" -> "Remplacement"
            "store_credit" -> "Avoir"
            else -> v
        }

        // ✅ Proof flags (provided -> FR)
        "photo_proof" -> if (v.lowercase() == "provided") "Fournie" else v
        "delivery_proof" -> if (v.lowercase() == "provided") "Fournie" else v

        else -> v
    }
}
 fun String.isYesValue(): Boolean =
    trim().lowercase() in setOf("oui", "yes", "true", "vrai", "ok")

fun uiLabelForKey(key: String?): String? = when (key?.trim()?.lowercase()) {

    // Commande / tracking
    "order_number" -> "N° de commande"
    "tracking_number" -> "N° de suivi"
    "invoice_number" -> "N° de facture"
    "amount" -> "Montant"
    "date_reference" -> "Date de référence"

    // Client
    "customer_email" -> "Email client"
    "customer_name" -> "Nom du client"
    "customer_phone" -> "Téléphone"
    "account_id" -> "Identifiant de compte"

    // Livraison
    "delivery_status" -> "Statut livraison"
    "delivery_address" -> "Adresse de livraison"
    "delivery_city" -> "Ville"
    "delivery_postcode" -> "Code postal"
    "delivery_recipient" -> "Destinataire"

    // Produit
    "product_name" -> "Produit"
    "product_sku" -> "Référence produit"
    "missing_product_name" -> "Manquant"

    // Incident
    "issue_type" -> "Type d’incident"
    "refund_preference" -> "Préférence de remboursement"
    "photo_proof" -> "Preuve photo"
    "delivery_proof" -> "Preuve de livraison"

    // Sécurité
    "login_identifier" -> "Identifiant utilisé"
    "recent_security_events" -> "Activité récente"

    // Autres
    "customer_note" -> "Note client"

    else -> null
}

fun DetectedFieldV2.uiLabel(): String {
    val k = keyNorm()
    return when (k) {
        // 🧾 COMMANDE
        "order_number", "order_id" -> "N° commande"
        "order_timeframe", "timeframe" -> "Période"

        // 👤 CLIENT
        "customer_name" -> "Client"
        "customer_email", "email" -> "Email"
        "customer_phone", "phone" -> "Tél"
        "account_id" -> "ID compte"

        // 📦 LIVRAISON
        "delivery_method" -> "Livraison"
        "delivery_status" -> "Statut livraison"
        "delivery_address" -> "Adresse"
        "delivery_city" -> "Ville"
        "delivery_postcode" -> "Code postal"
        "delivery_recipient" -> "Destinataire"
        "tracking_number" -> "N° suivi"

        // 💳 PAIEMENT
        "payment_amount", "paid_amount", "amount" -> "Montant"
        "payment_method" -> "Paiement"
        "invoice_number" -> "N° facture"
        "date_reference" -> "Date"

        // 🧺 PRODUIT / REMBOURSEMENT
        "product_name" -> "Produit"
        "product_sku" -> "SKU"
        "missing_product_name" -> "Manquant"
        "issue_type" -> "Type d’incident"
        "refund_preference" -> "Préférence"
        "photo_proof" -> "Preuve Photo"
        "delivery_proof" -> "Preuve livraison"

        "customer_note" -> "Note client"

        else -> label?.trim().orEmpty().ifBlank { k.ifBlank { "Champ" } }
    }
}

fun DetectedFieldV2.icon(): String {
    return when (keyNorm()) {
        "customer_name" -> "👤"
        "customer_email", "email" -> "✉️"
        "customer_phone", "phone" -> "📞"
        "account_id" -> "🪪"

        "order_number", "order_id" -> "🧾"
        "invoice_number" -> "🧾"
        "amount", "payment_amount", "paid_amount" -> "💳"
        "date_reference" -> "🗓️"

        "delivery_address" -> "📍"
        "delivery_status" -> "📦"
        "delivery_city" -> "🏙️"
        "delivery_postcode" -> "🔢"
        "delivery_recipient" -> "📦"
        "tracking_number" -> "🔗"
        "delivery_proof" -> "📄"
        "photo_proof" -> "📷"

        "product_name", "product_sku", "missing_product_name" -> "🧺"
        "issue_type" -> "⚠️"
        "refund_preference" -> "💶"

        else -> "🔎"
    }
}
fun DetectedFieldV2.groupSoft(): SoftGroup {
    return when (keyNorm()) {
        "customer_name", "customer_email", "email", "customer_phone", "phone", "account_id" -> SoftGroup.CUSTOMER
        "order_number", "order_id", "invoice_number", "date_reference", "order_timeframe", "timeframe", "missing_product_name" -> SoftGroup.COMMANDE
        "product_name", "product_sku", "issue_type", "refund_preference" -> SoftGroup.PRODUIT_INCIDENT
        "payment_method", "payment_amount", "paid_amount", "amount" -> SoftGroup.PAIEMENT
        "delivery_status", "delivery_method", "tracking_number", "delivery_recipient", "delivery_address", "delivery_city", "delivery_postcode" -> SoftGroup.LIVRAISON
        "photo_proof", "delivery_proof" -> SoftGroup.PREUVES
        "login_identifier", "recent_security_events" -> SoftGroup.OTHER
        else -> SoftGroup.OTHER
    }
}

/** Preuve photo / Preuve livraison (pour catégorie "Preuves" dans la sidebar). */
fun DetectedFieldV2.isPreuve(): Boolean =
    keyNorm() in setOf("photo_proof", "delivery_proof")

fun DetectedFieldV2.canCopy(): Boolean {
    val k = keyNorm()
    val v = value?.trim().orEmpty()
    if (v.isEmpty()) return false
    return k in setOf(
        "order_number", "order_id",
        "tracking_number",
        "customer_email", "email",
        "customer_phone", "phone",
        "delivery_address"
    )
}